import "../../../assets/css/mini.css";
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  Input,
  notification,
  Drawer,
  Spin,
  Collapse,
  Pagination,
  Button,
  Tag,
  Modal,
  Select,
  Form,
  Segmented,
  Empty,
  Tabs,
} from "antd";
import "swiper/css";
import { Link, useNavigate } from "react-router-dom";
import { LoadingOutlined, PhoneOutlined, ClockCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { DateTime } from "luxon";
import { getDistanceWithFallback } from "../../../utils/distance";
import useDeliverySocket from "../../../hooks/useDeliverySocket";
import NumberFormat from "react-number-format";
import {
  _get_all_restaurant_orders,
  _get_all_restaurant_child_orders,
  _report_restaurant_issue,
  _submit_buyer_help_request,
  _get_business_support_team,
} from "../../../common/axios_services";
import TopNav from "../../../components/top-nav";
import FooterDisplay from "../../../components/footer";
import {
  ExternalLink,
  Calendar,
  Hash,
  MapPin,
  Phone,
  CheckCircle,
  Package,
  CreditCard,
  Copy,
} from "lucide-react";
import { message } from "antd";
import {
  showSuccessNotification,
  showErrorNotification,
} from "../../../common/utils/notification";
import { useSelector } from "react-redux";
import { roundDisplayPrice } from "../../../utils/priceHelpers";
import PendingActivitiesModal from "../../../components/modals/PendingActivitiesModal";
import PendingActivitiesBanner from "../../../components/PendingActivitiesBanner";

const OrderHistory = () => {
  const restaurantCarts = useSelector((store) => store.restaurantCart.items);
  const restaurantId = useSelector(
    (store) => store.restaurantCart.restaurantId,
  );
  const businessType = useSelector(
    (store) => store.restaurantCart.businessType,
  );

  const openNotificationWithIcon = (type, message) => {
    notification[type]({
      description: message,
    });
  };
  const majorSpinnerIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;
  const [majorPageSpinner, setMajorPageSpinner] = useState(true);
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const [order_his, setOrder_his] = useState([]);
  const [per_page, setPer_page] = useState(10);
  const [total_trans, setTotal_trans] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [pendingModalVisible, setPendingModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [deliveryType, setDeliveryType] = useState("All");
  const [deliveryDate, setDeliveryDate] = useState("All");
  const [helpModalVisible, setHelpModalVisible] = useState(false);
  const [supportManager, setSupportManager] = useState(null);
  const [fetchingSupport, setFetchingSupport] = useState(false);

  const hasAutoOpenedUserRef = useRef(false);
  const activeUserPendingActivitiesCount = useMemo(() => {
    return (Array.isArray(order_his) ? order_his : []).filter((o) =>
      ["WAITING", "ACCEPTED", "READY", "IN_TRANSIT", "pending", "ready", "in_transit"].includes(
        o.status || o.delivery_status
      )
    ).length;
  }, [order_his]);

  useEffect(() => {
    if (!hasAutoOpenedUserRef.current && activeUserPendingActivitiesCount > 0) {
      setPendingModalVisible(true);
      hasAutoOpenedUserRef.current = true;
    }
  }, [activeUserPendingActivitiesCount]);

  useEffect(() => {
    const fetchSupport = async () => {
      const businessId =
        selectedOrder?.restaurant?.businessId ||
        selectedOrder?.restaurant?.businessInfo?.id ||
        selectedOrder?.restaurant?.id;
      if (businessId) {
        setFetchingSupport(true);
        try {
          const res = await _get_business_support_team(businessId);
          setSupportManager(res.data?.supportManager);
        } catch (err) {
          console.error("Error fetching support manager", err);
        } finally {
          setFetchingSupport(false);
        }
      }
    };
    if (isModalVisible) {
      fetchSupport();
    } else {
      setSupportManager(null);
    }
  }, [selectedOrder, isModalVisible]);

  const [liveTracking, setLiveTracking] = useState({
    distance: null,
    eta: null
  });

  const handleLocationUpdate = useCallback((data) => {
    if (selectedOrder?.delivery?.id && data.deliveryId === selectedOrder.delivery.id) {
      setLiveTracking({
        distance: data.distance,
        eta: data.eta
      });
    }
  }, [selectedOrder?.delivery?.id]);

  useDeliverySocket({
    onLocationUpdate: handleLocationUpdate
  });

  useEffect(() => {
    setLiveTracking({
      distance: null,
      eta: null
    });
  }, [selectedOrder?.id]);

  const [helpForm] = Form.useForm();
  const [helpSubmitting, setHelpSubmitting] = useState(false);
  const [tabCounts, setTabCounts] = useState({
    All: 0,
    Today: 0,
    Scheduled: 0,
    Completed: 0,
  });

  const showingAllTab = deliveryDate == "All";

  const fetchTabCounts = async () => {
    try {
      const counts = {};
      const tabKeys = [
        {
          key: "All",
          params: {
            deliveryType: deliveryType.toLowerCase(),
            status: "all",
            page: 1,
            limit: 1,
          },
        },
        {
          key: "Today",
          params: {
            deliveryType: deliveryType.toLowerCase(),
            status: "today",
            page: 1,
            limit: 1,
          },
        },
        {
          key: "Scheduled",
          params: {
            deliveryType: deliveryType.toLowerCase(),
            status: "scheduled",
            page: 1,
            limit: 1,
          },
        },
        {
          key: "Completed",
          params: {
            deliveryType: deliveryType.toLowerCase(),
            status: "delivered",
            page: 1,
            limit: 1,
          },
        },
      ];

      for (const tab of tabKeys) {
        try {
          const response = await _get_all_restaurant_child_orders(tab.params);
          counts[tab.key] = response?.data?.data?.pagination?.total || 0;
        } catch (err) {
          counts[tab.key] = 0;
        }
      }
      setTabCounts(counts);
    } catch (err) {
      console.error("Error fetching tab counts:", err);
    }
  };

  const allTransOrders = async (type = deliveryType, date = deliveryDate) => {
    try {
      setMajorPageSpinner(true);
      let allOrders;
      if (deliveryDate === "All") {
        allOrders = await _get_all_restaurant_orders({
          deliveryType: type.toLowerCase(),
          status: date.toLowerCase(),
          page: page,
          limit: per_page,
        });
      } else {
        allOrders = await _get_all_restaurant_child_orders({
          deliveryType: type.toLowerCase(),
          status:
            date.toLowerCase() === "completed"
              ? "delivered"
              : date.toLowerCase(),
          page: page,
          limit: per_page,
        });
      }
      const ordersData = allOrders?.data?.data;

      if (ordersData) {
        setOrder_his(ordersData.orders);
        setTotal_trans(ordersData?.pagination?.total);
      } else {
        setOrder_his([]);
      }
    } catch (err) {
      console.error("Error loading orders:", err);
      openNotificationWithIcon(
        "error",
        err.response.data.message || err.message,
      );
    } finally {
      setMajorPageSpinner(false);
    }
  };

  useEffect(() => {
    allTransOrders();
  }, [per_page, page, deliveryType, deliveryDate]);

  useEffect(() => {
    fetchTabCounts();
  }, [deliveryType]);

  const change_page = (selected_page, pageSize) => {
    setPage(selected_page);
    setPer_page(pageSize);
  };

  const handleViewClick = (record) => {
    setSelectedOrder(record);
    setIsModalVisible(true);
  };

  const isOrderOlderThan30Minutes = (order) => {
    const orderTime = new Date(order.createdAt || order.created_at);
    const now = new Date();
    const diffInMinutes = (now - orderTime) / (1000 * 60);
    return diffInMinutes > 30;
  };

  const handleReportRestaurantIssue = async (order) => {
    try {
      await _report_restaurant_issue(order.id);
      openNotificationWithIcon(
        "success",
        "Issue reported successfully. Support will contact you soon.",
      );
      allTransOrders();
    } catch (err) {
      openNotificationWithIcon(
        "error",
        "Failed to report issue. Please try again.",
      );
    }
  };

  const handleHelpSubmit = async () => {
    try {
      const values = await helpForm.validateFields();
      setHelpSubmitting(true);

      await _submit_buyer_help_request(selectedOrder.id, {
        orderId: selectedOrder.id,
        buyerId: selectedOrder.user?.id || selectedOrder.customerId,
        triggerType: values.triggerType,
        notes: values.notes,
      });

      showSuccessNotification(
        "Help request submitted. Support team has been notified.",
        "Success",
      );
      setHelpModalVisible(false);
      helpForm.resetFields();
    } catch (error) {
      showErrorNotification(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to submit help request",
        "Error",
      );
    } finally {
      setHelpSubmitting(false);
    }
  };

  return (
    <div className="wise9ja-content">
      <div>
        <Spin indicator={majorSpinnerIcon} spinning={majorPageSpinner}>
          <div className="homepage wise9ja-disp">
            <div className="homepage-history-header">
              <TopNav title="Your order history" />
            </div>
            <div className="hero-sect mt_2"></div>
            <div className="content-display-content">
              <div className="contain">
                <PendingActivitiesBanner
                  title="Pending Activities"
                  subtitle="Track active waiting, collection & in-transit orders"
                  buttonText="View Activities"
                  count={
                    order_his.filter((o) =>
                      ["WAITING", "ACCEPTED", "READY", "IN_TRANSIT", "pending", "ready", "in_transit"].includes(
                        o.status || o.delivery_status
                      )
                    ).length
                  }
                  onClick={() => setPendingModalVisible(true)}
                />
                <Tabs
                  activeKey={deliveryDate}
                  onChange={setDeliveryDate}
                  type="card"
                  className="support-tabs"
                >
                  <Tabs.TabPane tab={`All Orders (${tabCounts.All})`} key="All">
                    <div className="mt history-data-detail">
                      <div className="contai">
                        {!majorPageSpinner ? (
                          <div className="mobile topup-data">
                            {order_his.length > 0 ? (
                              <>
                                <Collapse defaultActiveKey={[0]}>
                                  {order_his.map((order, index) => (
                                    <Collapse.Panel
                                      showArrow={false}
                                      key={index}
                                      header={
                                        <p style={{ display: "inline" }}>
                                          Ordered on{" "}
                                          {new Date(
                                            order.createdAt || order.created_at,
                                          ).toLocaleString()}
                                        </p>
                                      }
                                    >
                                      <div>
                                        <ul className="mobile-list-flex">
                                          <li>
                                            <span>Date: </span>
                                            <span>
                                              {new Date(
                                                order.createdAt ||
                                                order.created_at,
                                              ).toLocaleString()}
                                            </span>
                                          </li>
                                          <li>
                                            <span>Ref Code: </span><span className="text-lg font-extrabold text-gray-900">#{order.orderCode || "N/A"}</span>
                                          </li>
                                          <li>
                                            <span>Business Type: </span>
                                            <span>{order.businessType}</span>
                                          </li>
                                          <li>
                                            <span>Items: </span>
                                            <span>
                                              {Array.isArray(
                                                order.orderItems || order.item,
                                              )
                                                ? (
                                                  order.orderItems ||
                                                  order.item
                                                )
                                                  .map(
                                                    (item) =>
                                                      item.quantity || 0,
                                                  )
                                                  .reduce((a, b) => a + b, 0)
                                                : 0}
                                            </span>
                                          </li>
                                          <li>
                                            <span>Status: </span>
                                            <span>
                                              {order.status === "WAITING" ? (
                                                <Tag color="orange">
                                                  Waiting
                                                </Tag>
                                              ) : order.status ===
                                                "CONFIRMED" ||
                                                order.status === "ACCEPTED" ||
                                                order?.status?.includes(
                                                  "READY",
                                                ) ||
                                                order.status?.includes(
                                                  "PICKED",
                                                ) ? (
                                                <Tag color="green">
                                                  {order.status.toLowerCase()}
                                                </Tag>
                                              ) : order.status ===
                                                "REJECTED" ? (
                                                <Tag color="red">
                                                  {order.status.toLowerCase()}
                                                </Tag>
                                              ) : (
                                                <Tag color="blue">
                                                  {order.status.toLowerCase()}
                                                </Tag>
                                              )}
                                            </span>
                                          </li>
                                        </ul>
                                        <div className="mt-3">
                                          <Button
                                            onClick={() =>
                                              handleViewClick(order)
                                            }
                                            type="link"
                                          >
                                            View Order
                                          </Button>
                                          {order.status.toLowerCase() ===
                                            "waiting" && (
                                              <Button
                                                type="default"
                                                size="small"
                                                onClick={() =>
                                                  handleReportRestaurantIssue(
                                                    order,
                                                  )
                                                }
                                                style={{ marginLeft: "8px" }}
                                              >
                                                Restaurant Not Responding
                                              </Button>
                                            )}
                                        </div>
                                      </div>
                                    </Collapse.Panel>
                                  ))}
                                </Collapse>
                                <div style={{ marginTop: "2%" }}></div>
                                <Pagination
                                  showSizeChanger
                                  current={page}
                                  total={total_trans}
                                  showTotal={(total, range) =>
                                    `${range[0]}-${range[1]} of ${total} orders`
                                  }
                                  defaultPageSize={per_page}
                                  onChange={change_page}
                                />
                              </>
                            ) : (
                              <Empty
                                image={
                                  <Package
                                    style={{ fontSize: "48px", color: "#ddd" }}
                                  />
                                }
                                description={
                                  <div>
                                    <p
                                      style={{
                                        fontSize: "18px",
                                        margin: "0",
                                        color: "#666",
                                      }}
                                    >
                                      {deliveryDate === "All"
                                        ? "No orders found"
                                        : deliveryDate === "Today"
                                          ? "No orders for today"
                                          : deliveryDate === "Scheduled"
                                            ? "No scheduled orders"
                                            : deliveryDate === "Completed"
                                              ? "No completed orders"
                                              : "No orders found"}
                                    </p>
                                    <p
                                      style={{
                                        fontSize: "14px",
                                        margin: "8px 0 0 0",
                                        color: "#999",
                                      }}
                                    >
                                      {deliveryDate === "All"
                                        ? "You haven't placed any orders yet."
                                        : deliveryDate === "Today"
                                          ? "You don't have any orders scheduled for today."
                                          : deliveryDate === "Scheduled"
                                            ? "You don't have any scheduled orders."
                                            : deliveryDate === "Completed"
                                              ? "You don't have any completed orders."
                                              : "You haven't placed any orders yet."}
                                    </p>
                                  </div>
                                }
                              />
                            )}
                          </div>
                        ) : (
                          ""
                        )}
                      </div>
                    </div>
                  </Tabs.TabPane>

                  {/* <Tabs.TabPane tab={`Today (${tabCounts.Today})`} key="Today">
                    <div className="mt history-data-detail">
                      <div className="contai">
                        {!majorPageSpinner ? (
                          <div className="mobile topup-data">
                            {order_his.length > 0 ? (
                              <>
                                <Collapse defaultActiveKey={[0]}>
                                  {order_his.map((order, index) => (
                                    <Collapse.Panel
                                      showArrow={false}
                                      key={index}
                                      header={
                                        <p style={{ display: "inline" }}>
                                          Ordered on{" "}
                                          {new Date(
                                            order.createdAt || order.created_at,
                                          ).toLocaleString()}
                                        </p>
                                      }
                                    >
                                      <div>
                                        <ul className="mobile-list-flex">
                                          <li>
                                            <span>Date: </span>
                                            <span>
                                              {new Date(
                                                order.createdAt ||
                                                order.created_at,
                                              ).toLocaleString()}
                                            </span>
                                          </li>
                                          <li>
                                            <span>Ref Code: </span><span className="text-lg font-extrabold text-gray-900">#{order.orderCode || "N/A"}</span>
                                          </li>
                                          <li>
                                            <span>Items: </span>
                                            <span>
                                              {Array.isArray(
                                                order.orderItems || order.item,
                                              )
                                                ? (
                                                  order.orderItems ||
                                                  order.item
                                                )
                                                  .map(
                                                    (item) =>
                                                      item.quantity || 0,
                                                  )
                                                  .reduce((a, b) => a + b, 0)
                                                : 0}
                                            </span>
                                          </li>
                                          <li>
                                            <span>Status: </span>
                                            <span>
                                              {order.status === "WAITING" ? (
                                                <Tag color="orange">
                                                  Waiting
                                                </Tag>
                                              ) : (
                                                <Tag color="green">
                                                  {order.status?.toLowerCase() ||
                                                    "N/A"}
                                                </Tag>
                                              )}
                                            </span>
                                          </li>
                                          <li>
                                            <span>Total Amount: </span>
                                            <span>
                                              ₦{roundDisplayPrice(order.totalAmount || 0).toFixed(2)}
                                            </span>
                                          </li>
                                        </ul>
                                        <div className="mt-3">
                                          <Button
                                            onClick={() =>
                                              handleViewClick(order)
                                            }
                                            type="link"
                                          >
                                            View Order
                                          </Button>
                                        </div>
                                      </div>
                                    </Collapse.Panel>
                                  ))}
                                </Collapse>
                                <div style={{ marginTop: "2%" }}></div>
                                <Pagination
                                  showSizeChanger
                                  current={page}
                                  total={total_trans}
                                  showTotal={(total, range) =>
                                    `${range[0]}-${range[1]} of ${total}`
                                  }
                                  defaultPageSize={per_page}
                                  onChange={change_page}
                                />
                              </>
                            ) : (
                              <Empty
                                description="No orders for today"
                                style={{
                                  marginTop: "50px",
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                  alignItems: "center",
                                }}
                              ></Empty>
                            )}
                          </div>
                        ) : (
                          ""
                        )}
                      </div>
                    </div>
                  </Tabs.TabPane> */}

                  <Tabs.TabPane
                    tab={`Scheduled (${tabCounts.Scheduled})`}
                    key="Scheduled"
                  >
                    <div className="mt history-data-detail">
                      <div className="contai">
                        {!majorPageSpinner ? (
                          <div className="mobile topup-data">
                            {order_his.length > 0 ? (
                              <>
                                <Collapse defaultActiveKey={[0]}>
                                  {order_his.map((order, index) => (
                                    <Collapse.Panel
                                      showArrow={false}
                                      key={index}
                                      header={
                                        <p style={{ display: "inline" }}>
                                          Ordered on{" "}
                                          {new Date(
                                            order.createdAt || order.created_at,
                                          ).toLocaleString()}
                                        </p>
                                      }
                                    >
                                      <div>
                                        <ul className="mobile-list-flex">
                                          <li>
                                            <span>Date: </span>
                                            <span>
                                              {new Date(
                                                order.createdAt ||
                                                order.created_at,
                                              ).toLocaleString()}
                                            </span>
                                          </li>
                                          <li>
                                            <span>Ref Code: </span><span className="text-lg font-extrabold text-gray-900">#{order.orderCode || "N/A"}</span>
                                          </li>
                                          <li>
                                            <span>Items: </span>
                                            <span>
                                              {Array.isArray(
                                                order.orderItems || order.item,
                                              )
                                                ? (
                                                  order.orderItems ||
                                                  order.item
                                                )
                                                  .map(
                                                    (item) =>
                                                      item.quantity || 0,
                                                  )
                                                  .reduce((a, b) => a + b, 0)
                                                : 0}
                                            </span>
                                          </li>
                                          <li>
                                            <span>Status: </span>
                                            <span>
                                              {order.status === "WAITING" ? (
                                                <Tag color="orange">
                                                  Waiting
                                                </Tag>
                                              ) : (
                                                <Tag color="green">
                                                  {order.status?.toLowerCase() ||
                                                    "N/A"}
                                                </Tag>
                                              )}
                                            </span>
                                          </li>
                                          <li>
                                            <span>Total Amount: </span>
                                            <span>
                                              ₦{roundDisplayPrice(order.totalAmount || 0).toFixed(2)}
                                            </span>
                                          </li>
                                        </ul>
                                        <div className="mt-3">
                                          <Button
                                            onClick={() =>
                                              handleViewClick(order)
                                            }
                                            type="link"
                                          >
                                            View Order
                                          </Button>
                                        </div>
                                      </div>
                                    </Collapse.Panel>
                                  ))}
                                </Collapse>
                                <div style={{ marginTop: "2%" }}></div>
                                <Pagination
                                  showSizeChanger
                                  current={page}
                                  total={total_trans}
                                  showTotal={(total, range) =>
                                    `${range[0]}-${range[1]} of ${total}`
                                  }
                                  defaultPageSize={per_page}
                                  onChange={change_page}
                                />
                              </>
                            ) : (
                              <Empty
                                description="No scheduled orders"
                                style={{
                                  marginTop: "50px",
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                  alignItems: "center",
                                }}
                              ></Empty>
                            )}
                          </div>
                        ) : (
                          ""
                        )}
                      </div>
                    </div>
                  </Tabs.TabPane>

                  {/* <Tabs.TabPane
                    tab={`Completed (${tabCounts.Completed})`}
                    key="Completed"
                  >
                    <div className="mt history-data-detail">
                      <div className="contai">
                        {!majorPageSpinner ? (
                          <div className="mobile topup-data">
                            {order_his.length > 0 ? (
                              <>
                                <Collapse defaultActiveKey={[0]}>
                                  {order_his.map((order, index) => (
                                    <Collapse.Panel
                                      showArrow={false}
                                      key={index}
                                      header={
                                        <p style={{ display: "inline" }}>
                                          Ordered on{" "}
                                          {new Date(
                                            order.createdAt || order.created_at,
                                          ).toLocaleString()}
                                        </p>
                                      }
                                    >
                                      <div>
                                        <ul className="mobile-list-flex">
                                          <li>
                                            <span>Date: </span>
                                            <span>
                                              {new Date(
                                                order.createdAt ||
                                                order.created_at,
                                              ).toLocaleString()}
                                            </span>
                                          </li>
                                          <li>
                                            <span>Ref Code: </span><span className="text-lg font-extrabold text-gray-900">#{order.orderCode || "N/A"}</span>
                                          </li>
                                          <li>
                                            <span>Items: </span>
                                            <span>
                                              {Array.isArray(
                                                order.orderItems || order.item,
                                              )
                                                ? (
                                                  order.orderItems ||
                                                  order.item
                                                )
                                                  .map(
                                                    (item) =>
                                                      item.quantity || 0,
                                                  )
                                                  .reduce((a, b) => a + b, 0)
                                                : 0}
                                            </span>
                                          </li>
                                          <li>
                                            <span>Status: </span>
                                            <span>
                                              <Tag color="green">
                                                {order.status?.toLowerCase() ||
                                                  "N/A"}
                                              </Tag>
                                            </span>
                                          </li>
                                          <li>
                                            <span>Total Amount: </span>
                                            <span>
                                              ₦{roundDisplayPrice(order.totalAmount || 0).toFixed(2)}
                                            </span>
                                          </li>
                                        </ul>
                                        <div className="mt-3">
                                          <Button
                                            onClick={() =>
                                              handleViewClick(order)
                                            }
                                            type="link"
                                          >
                                            View Order
                                          </Button>
                                        </div>
                                      </div>
                                    </Collapse.Panel>
                                  ))}
                                </Collapse>
                                <div style={{ marginTop: "2%" }}></div>
                                <Pagination
                                  showSizeChanger
                                  current={page}
                                  total={total_trans}
                                  showTotal={(total, range) =>
                                    `${range[0]}-${range[1]} of ${total}`
                                  }
                                  defaultPageSize={per_page}
                                  onChange={change_page}
                                />
                              </>
                            ) : (
                              <Empty
                                description="No completed orders"
                                style={{
                                  marginTop: "50px",
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                  alignItems: "center",
                                }}
                              ></Empty>
                            )}
                          </div>
                        ) : (
                          ""
                        )}
                      </div>
                    </div>
                  </Tabs.TabPane> */}
                </Tabs>
              </div>
            </div>
          </div>
        </Spin>
      </div>
      <FooterDisplay />

      <Drawer
        title={
          <div style={{ fontSize: "20px", fontWeight: "bold", color: "#333" }}>
            Order Details
          </div>
        }
        placement="bottom"
        onClose={() => setIsModalVisible(false)}
        open={isModalVisible}
        width={720}
        height="100%"
        bodyStyle={{ backgroundColor: "#fafafa", padding: "20px" }}
      >
        {selectedOrder && (
          <div
            className="space-y-6"
            style={{ maxHeight: "75vh", overflowY: "auto", padding: "10px 0" }}
          >
            <div className="mb-6">
              <div className="space-y-4">
                <div className="flex items-center mb-5">
                  <Calendar className="w-6 h-6 text-blue-500 mr-3" />
                  <div className="flex flex-col">
                    <p className="text-lg text-gray-500 font-medium uppercase tracking-wide">
                      Date
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {new Date(
                        selectedOrder.createdAt || selectedOrder.created_at,
                      ).toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center mb-5">
                  <Hash className="w-6 h-6 text-green-500 mr-3" />
                  <div className="flex flex-col">
                    <p className="text-lg text-gray-500 font-medium uppercase tracking-wide">
                      Ref Code
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      #{selectedOrder.orderCode || selectedOrder.track_id}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Group order items by deliveryDateType */}
            {(() => {
              const groupedItems = (selectedOrder.orderItems || []).reduce(
                (acc, item) => {
                  const deliveryType = item.deliveryDateType || "same_day";
                  if (!acc[deliveryType]) {
                    acc[deliveryType] = [];
                  }
                  acc[deliveryType].push(item);
                  return acc;
                },
                {},
              );

              const deliveryGroups = Object.keys(groupedItems);

              return deliveryGroups.map((groupKey) => {
                const items = groupedItems[groupKey];
                const itemCount = items.length;
                const totalAmount = items.reduce(
                  (sum, item) =>
                    sum + parseFloat(item.priceWithMarkup) * item.quantity,
                  0,
                );

                if (groupKey === "same_day") {
                  return (
                    <div key={groupKey} className="mb-6">
                      <h3 className="text-3xl font-bold text-gray-900">
                        You'll{" "}
                        {selectedOrder.deliveryType?.toLowerCase() === "pickup"
                          ? "pickup"
                          : "receive delivery of"}{" "}
                        {itemCount} item
                        {itemCount !== 1 ? "s" : ""} today
                      </h3>

                      {/* Items List */}
                      <div className="bg-blue-50 p-4 rounded-lg mb-4 border border-blue-100">
                        {items.map((item) => (
                          <div
                            key={item.id}
                            className="flex justify-between items-center py-2 border-b border-gray-200 last:border-b-0"
                          >
                            <div className="flex items-center mb-5">
                              <Package className="w-4 h-4 text-gray-500 mr-2" />
                              <span className="text-2xl text-gray-700">
                                {item.quantity}x {item.menuItem?.name || "Item"}
                              </span>
                            </div>
                            <span className="text-2xl font-medium text-gray-900">
                              ₦
                              {roundDisplayPrice(
                                parseFloat(item.priceWithMarkup || item.price || 0) * item.quantity
                              ).toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>

                      {(selectedOrder.status === "WAITING" ||
                        selectedOrder.status === "ACCEPTED" ||
                        selectedOrder.status?.includes("READY")) &&
                        selectedOrder.pickupCode && (
                          <div className="flex items-center mb-5">
                            <CheckCircle className="w-6 h-6 text-green-500 mr-3" />
                            <div className="flex flex-col flex-1">
                              <p className="text-lg text-gray-500 font-medium uppercase tracking-wide">
                                Collection code
                              </p>
                              <div className="flex items-center gap-3">
                                <p className="text-2xl font-bold text-green-600">
                                  {selectedOrder.pickupCode}
                                </p>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(
                                      selectedOrder.pickupCode,
                                    );
                                    message.success("Code copied!");
                                  }}
                                  className="p-2 hover:bg-gray-100 rounded-md transition"
                                  title="Copy pickup code"
                                >
                                  <Copy className="w-5 h-5 text-gray-600 hover:text-green-600" />
                                </button>
                              </div>
                            </div>
                          </div>
                        )}

                      {(selectedOrder.status === "WAITING" ||
                        selectedOrder.status === "ACCEPTED" ||
                        selectedOrder.status?.includes("READY") ||
                        selectedOrder.status === "IN_TRANSIT") &&
                        selectedOrder.delivery?.buyerCode && (
                          <div className="flex items-center mb-5">
                            <CheckCircle className="w-6 h-6 text-blue-500 mr-3" />
                            <div className="flex flex-col flex-1">
                              <p className="text-lg text-gray-500 font-medium uppercase tracking-wide">
                                Delivery Code
                              </p>
                              <div className="flex items-center gap-3">
                                <p className="text-2xl font-bold text-blue-600">
                                  {selectedOrder.delivery.buyerCode}
                                </p>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(
                                      selectedOrder.delivery.buyerCode,
                                    );
                                    message.success("Code copied!");
                                  }}
                                  className="p-2 hover:bg-gray-100 rounded-md transition"
                                  title="Copy delivery code"
                                >
                                  <Copy className="w-5 h-5 text-gray-600 hover:text-blue-600" />
                                </button>
                              </div>
                            </div>
                          </div>
                        )}

                      <div className="flex items-center mb-5">
                        <MapPin className="w-6 h-6 text-red-500 mr-3" />
                        <div className="flex flex-col">
                          <p className="text-lg text-gray-500 font-medium uppercase tracking-wide">
                            {selectedOrder.deliveryType?.toLowerCase() ===
                              "pickup"
                              ? "Pick-up"
                              : "Delivery"}{" "}
                            address
                          </p>
                          <p className="text-2xl font-medium text-gray-800">
                            {selectedOrder.deliveryType?.toLowerCase() ===
                              "pickup"
                              ? selectedOrder.restaurant?.businessInfo
                                ?.pickupAddress
                              : selectedOrder?.savedAddress?.address || "N/A"}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center mb-5">
                        <Phone className="w-6 h-6 text-blue-500 mr-3" />
                        <div className="flex flex-col">
                          <p className="text-lg text-gray-500 font-medium uppercase tracking-wide">
                            Restaurant Name & Telephone
                          </p>
                          <p className="text-2xl font-medium text-gray-800">
                            {selectedOrder.restaurant?.businessInfo?.name ||
                              "N/A"}
                            /
                            {selectedOrder.restaurant?.businessInfo
                              ?.phoneNumber || "N/A"}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center mb-5">
                        <CheckCircle className="w-6 h-6 text-orange-500 mr-3" />
                        <div className="flex flex-col">
                          <p className="text-lg text-gray-500 font-medium uppercase tracking-wide">
                            Order status
                          </p>
                          <p className="text-2xl font-medium capitalize text-gray-800">
                            {selectedOrder.status?.toLowerCase() || "N/A"}
                          </p>
                        </div>
                      </div>

                      <div>
                        <div className="text-xs text-gray-600 font-semibold mb-2">
                          Live Tracker
                        </div>
                        {selectedOrder?.delivery?.trackerLink &&
                          selectedOrder.status.toLowerCase() !== "delivered" ? (
                          <div className="bg-white border border-blue-100 rounded-xl p-4 shadow-sm">
                            <div className="grid grid-cols-2 gap-4 mb-4">
                              <div className="flex items-center space-x-3">
                                <div className="bg-blue-100 p-2 rounded-lg">
                                  <MapPin className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                  <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400">Distance</p>
                                  <p className="text-lg font-bold text-gray-900">
                                    {liveTracking.distance ||
                                      getDistanceWithFallback(
                                        selectedOrder.delivery.trackerLink,
                                        selectedOrder.delivery.distance
                                      )
                                    } km
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-3">
                                <div className="bg-green-100 p-2 rounded-lg">
                                  <Calendar className="w-5 h-5 text-green-600" />
                                </div>
                                <div>
                                  <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400">Arrival</p>
                                  <p className="text-lg font-bold text-gray-900">
                                    {liveTracking.eta ?
                                      DateTime.fromISO(liveTracking.eta).toFormat("h:mm a") :
                                      (selectedOrder?.delivery?.eta
                                        ? DateTime.fromISO(selectedOrder.delivery.eta).toFormat("h:mm a")
                                        : "...")
                                    }
                                  </p>
                                </div>
                              </div>
                            </div>

                            <button
                              onClick={() => window.open(selectedOrder.delivery.trackerLink, '_blank')}
                              className="w-full py-3 bg-green-600 text-white rounded-xl text-base font-bold shadow-md hover:bg-green-700 active:scale-[0.98] transition-all flex items-center justify-center space-x-2"
                            >
                              <MapPin className="w-5 h-5" />
                              <span>VIEW LIVE MAP</span>
                            </button>

                            <div className="mt-4 pt-4 border-t border-gray-50 flex items-center justify-center">
                              <div className="flex items-center space-x-2 text-blue-600 animate-pulse">
                                <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                                <span className="text-[10px] font-bold uppercase tracking-widest text-blue-500">Biker is on the move...</span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="p-3 bg-gray-100 rounded text-gray-600">
                            No tracker available
                          </div>
                        )}
                      </div>

                      <div className="flex items-center mb-5">
                        <CreditCard className="w-6 h-6 text-green-500 mr-3" />
                        <div className="flex flex-col">
                          <p className="text-lg text-gray-500 font-medium uppercase tracking-wide">
                            Total amount (Inc delivery fee, less discount)
                          </p>
                          <p className="text-2xl font-bold text-green-600">
                            <span className="small_currency">NGN</span>{" "}
                            <NumberFormat
                              thousandSeparator={true}
                              prefix={""}
                              className="numeric"
                              displayType={"text"}
                              value={roundDisplayPrice(selectedOrder.totalAmount || 0)}
                            />{" "}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                } else if (groupKey === "next_day") {
                  const deliveryDate = selectedOrder.scheduledDeliveryDate
                    ? dayjs(selectedOrder.scheduledDeliveryDate).format(
                      "ddd MMM D YYYY",
                    )
                    : "scheduled date";

                  return (
                    <div key={groupKey} className="mb-6">
                      <h3 className="text-3xl mb-5 font-bold text-gray-900">
                        You'll{" "}
                        {selectedOrder.deliveryType?.toLowerCase() === "pickup"
                          ? "pickup"
                          : "receive delivery of"}{" "}
                        {itemCount} item
                        {itemCount !== 1 ? "s" : ""} on {deliveryDate}
                      </h3>

                      {/* Items List */}
                      <div className="bg-blue-50 p-4 rounded-lg mb-4 border border-blue-100">
                        {items.map((item) => (
                          <div
                            key={item.id}
                            className="flex justify-between items-center py-2 border-b border-gray-200 last:border-b-0"
                          >
                            <div className="flex items-center mb-5">
                              <Package className="w-4 h-4 text-gray-500 mr-2" />
                              <span className="text-2xl text-gray-700">
                                {item.quantity}x {item.menuItem?.name || "Item"}
                              </span>
                            </div>
                            <span className="text-2xl font-medium text-gray-900">
                              ₦
                              {roundDisplayPrice(
                                parseFloat(item.priceWithMarkup || item.price || 0) * item.quantity
                              ).toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>

                      {(selectedOrder.status === "ACCEPTED" ||
                        selectedOrder.status.includes("READY")) &&
                        selectedOrder.pickupCode && (
                          <div className="flex items-center mb-5">
                            <CheckCircle className="w-6 h-6 text-green-500 mr-3" />
                            <div className="flex flex-col">
                              <p className="text-lg text-gray-500 font-medium uppercase tracking-wide">
                                Collection code
                              </p>
                              <div className="flex items-center gap-3">
                                <p className="text-2xl font-bold text-green-600">
                                  {selectedOrder.pickupCode}
                                </p>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(
                                      selectedOrder.pickupCode,
                                    );
                                    message.success("Code copied!");
                                  }}
                                  className="p-2 hover:bg-gray-100 rounded-md transition"
                                  title="Copy pickup code"
                                >
                                  <Copy className="w-5 h-5 text-gray-600 hover:text-green-600" />
                                </button>
                              </div>
                            </div>
                          </div>
                        )}

                      <div className="flex items-center mb-5">
                        <MapPin className="w-6 h-6 text-red-500 mr-3" />
                        <div className="flex flex-col">
                          <p className="text-lg text-gray-500 font-medium uppercase tracking-wide">
                            {selectedOrder.deliveryType?.toLowerCase() ===
                              "pickup"
                              ? "Pick-up"
                              : "Delivery"}{" "}
                            address
                          </p>
                          <p className="text-2xl font-medium text-gray-800">
                            {selectedOrder.deliveryType?.toLowerCase() ===
                              "pickup"
                              ? selectedOrder.restaurant?.businessInfo
                                ?.pickupAddress
                              : selectedOrder?.savedAddress?.address || "N/A"}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center mb-5">
                        <Phone className="w-6 h-6 text-blue-500 mr-3" />
                        <div className="flex flex-col">
                          <p className="text-lg text-gray-500 font-medium uppercase tracking-wide">
                            Restaurant Name & Telephone
                          </p>
                          <p className="text-2xl font-medium text-gray-800">
                            {selectedOrder.restaurant?.businessInfo?.name ||
                              "N/A"}
                            /
                            {selectedOrder.restaurant?.businessInfo
                              ?.phoneNumber || "N/A"}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center mb-5">
                        <CheckCircle className="w-6 h-6 text-orange-500 mr-3" />
                        <div className="flex flex-col">
                          <p className="text-lg text-gray-500 font-medium uppercase tracking-wide">
                            Order status
                          </p>
                          <p className="text-2xl font-medium capitalize text-gray-800">
                            {selectedOrder.status?.toLowerCase() || "N/A"}
                          </p>
                        </div>
                      </div>

                      <div>
                        <div className="text-xs text-gray-600 font-semibold mb-2">
                          Live Tracker
                        </div>
                        {selectedOrder?.delivery?.trackerLink &&
                          selectedOrder.status.toLowerCase() !== "delivered" ? (
                          <div className="bg-white border border-blue-100 rounded-xl p-4 shadow-sm">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <div className="bg-blue-100 p-2 rounded-lg">
                                  <MapPin className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-500">Delivery Distance</p>
                                  <p className="text-xl font-bold text-gray-900">
                                    {liveTracking.distance ||
                                      getDistanceWithFallback(
                                        selectedOrder.delivery.trackerLink,
                                        selectedOrder.delivery.distance
                                      )
                                    } km
                                  </p>
                                </div>
                              </div>
                              <button
                                onClick={() => window.open(selectedOrder.delivery.trackerLink, '_blank')}
                                className="w-full mt-4 py-3 bg-green-600 text-white rounded-xl text-base font-bold shadow-md hover:bg-green-700 transition-all flex items-center justify-center space-x-2"
                              >
                                <MapPin className="w-5 h-5" />
                                <span>View Live Map</span>
                              </button>
                            </div>
                            <div className="flex items-center justify-between mt-4">
                              <div className="flex items-center space-x-3">
                                <div className="bg-green-100 p-2 rounded-lg">
                                  <Calendar className="w-5 h-5 text-green-600" />
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-medium text-gray-500">Estimated Arrival</p>
                                  <p className="text-xl font-bold text-gray-900">
                                    {liveTracking.eta ?
                                      DateTime.fromISO(liveTracking.eta).toFormat("h:mm a") :
                                      (selectedOrder?.delivery?.eta
                                        ? DateTime.fromISO(selectedOrder.delivery.eta).toFormat("h:mm a")
                                        : "Calculating...")
                                    }
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="mt-4 pt-4 border-t border-gray-50 flex items-center justify-center">
                              <div className="flex items-center space-x-2 text-blue-600 animate-pulse">
                                <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                                <span className="text-sm font-medium">Biker is on the move...</span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="p-3 bg-gray-100 rounded text-gray-600">
                            No tracker available
                          </div>
                        )}
                      </div>

                      <div className="flex items-center mb-5">
                        <CreditCard className="w-6 h-6 text-green-500 mr-3" />
                        <div className="flex flex-col">
                          <p className="text-lg text-gray-500 font-medium uppercase tracking-wide">
                            Total amount (Inc delivery fee, less discount)
                          </p>
                          <p className="text-2xl font-bold text-green-600">
                            <span className="small_currency">NGN</span>{" "}
                            <NumberFormat
                              thousandSeparator={true}
                              prefix={""}
                              className="numeric"
                              displayType={"text"}
                              value={roundDisplayPrice(selectedOrder.totalAmount || 0)}
                            />{" "}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              });
            })()}

            {/* Help Button */}
            {["READY"].includes(selectedOrder.status?.toUpperCase()) && (
              <Button
                type="primary"
                icon={<PhoneOutlined />}
                loading={fetchingSupport}
                onClick={() => {
                  if (supportManager?.phoneNumber) {
                    window.open(`tel:${supportManager.phoneNumber}`);
                  } else {
                    message.info(
                      "Support contact not available. Please try again later.",
                    );
                  }
                }}
                size="large"
                block
                style={{ marginBottom: 16 }}
              >
                Need Help?
              </Button>
            )}

            {showingAllTab && (
              <Link to={`/Restaurant/${selectedOrder.id}/checkout_successful`}>
                <button className="py-4 px-6 rounded-lg focus:outline-none bg-black w-full font-bold text-white text-2xl flex items-center mb-5 justify-center transition-all duration-200">
                  <span className="flex items-center p-2 gap-2">
                    Order Details
                  </span>
                </button>
              </Link>
            )}
          </div>
        )}
      </Drawer>
      <PendingActivitiesModal
        visible={pendingModalVisible}
        onClose={() => setPendingModalVisible(false)}
        activeOrders={order_his}
        onRefresh={allTransOrders}
        onViewOrder={(order) => {
          setPendingModalVisible(false);
          handleViewClick(order);
        }}
      />
    </div>
  );
};

export default OrderHistory;
